import { CQAppConstant } from '../app-constants';

/**
 * Result of tag validation with normalized output.
 */
export type TagValidationResult = {
    isValid: boolean;
    tags: string[];
    normalized: string;
    error?: string;
};

/**
 * Split a comma-separated tag string into trimmed tag names.
 */
export function parseTagNames(value?: string): string[] {
    if (!value) return [];
    return value
        .split(',')
        .map(tag => tag.trim())
        .filter(tag => tag.length > 0);
}

/**
 * Normalize a tag string by trimming and re-joining with ", ".
 */
export function normalizeTagNames(value?: string): string {
    const tags = parseTagNames(value);
    const normalizedTags = tags.map(tag => tag.replace(/\s+/g, ' '));
    return normalizedTags.join(', ');
}

/**
 * Validate tags for count, length, and duplicates (case-insensitive).
 */
export function validateTagNames(value?: string): TagValidationResult {
    const tags = parseTagNames(value);
    const normalizedTags = tags.map(tag => tag.replace(/\s+/g, ' '));
    const normalized = normalizedTags.join(', ');

    if (value && tags.length === 0) {
        return {
            isValid: false,
            tags,
            normalized,
            error: 'Tags cannot be only whitespace.',
        };
    }

    if (tags.length > CQAppConstant.TAG_MAX_COUNT) {
        return {
            isValid: false,
            tags: normalizedTags,
            normalized,
            error: `You can add up to ${CQAppConstant.TAG_MAX_COUNT} tags only.`,
        };
    }

    for (const tag of normalizedTags) {
        if (tag.length < 3) {
            return {
                isValid: false,
                tags: normalizedTags,
                normalized,
                error: 'Each tag must be at least 2 characters long.',
            };
        }
        if (tag.length > CQAppConstant.TAG_MAX_LENGTH) {
            return {
                isValid: false,
                tags: normalizedTags,
                normalized,
                error: `Each tag must be ${CQAppConstant.TAG_MAX_LENGTH} characters or fewer.`,
            };
        }
    }

    const normalizedSet = new Set<string>();
    for (const tag of normalizedTags) {
        const key = tag.toLowerCase();
        if (normalizedSet.has(key)) {
            return {
                isValid: false,
                tags: normalizedTags,
                normalized,
                error: 'Duplicate tags are not allowed.',
            };
        }
        normalizedSet.add(key);
    }

    return { isValid: true, tags: normalizedTags, normalized };
}

